import styled from 'styled-components';
import { Link } from 'react-router-dom';
import PageMetaLoader from '../components/PageMetaLoader';
import CtaBanner from '../components/CtaBanner';
import { getFeaturedProducts } from '../assets/products';
import { useI18n } from '../i18n';
import { en, km } from './SolutionsPage.data';

const journeySolutions = [
  {
    title: 'Sales',
    image: '/images/solutions/sales-solution.jpg',
    link: '/solutions/sales',
  },
  {
    title: 'HR',
    image: '/images/solutions/hr-solution.jpg',
    link: '/solutions/hr',
  },
  {
    title: 'Inventory',
    image: '/images/solutions/inventory-solution.jpg',
    link: '/solutions/inventory',
  },
  {
    title: 'Accounting',
    image: '/images/solutions/accounting-solution.jpg',
    link: '/solutions/accounting',
  },
  {
    title: 'CRM',
    image: '/images/solutions/crm-solution.jpg',
    link: '/solutions/crm',
  },
  {
    title: 'Operations',
    image: '/images/solutions/operations-solution.jpg',
    link: '/solutions/operations',
  },
];

const SolutionsPage = () => {
  const { locale, localePath } = useI18n();
  const t = locale === 'km' ? km : en;
  const modules = getFeaturedProducts(locale);

  return (
    <>
      <PageMetaLoader pageUrl="solutions" />
      <SolutionsContainer>
        <Intro>
          <Kicker>{t.kicker}</Kicker>
          <PageTitle>{t.title}</PageTitle>
          <Lead>{t.lead}</Lead>
        </Intro>

        <SectionLabel>{t.journeysLabel}</SectionLabel>
        <SolutionsGrid>
          {journeySolutions.map((solution, index) => {
            const copy = t.journeySolutions[index];
            return (
              <SolutionCard key={solution.title}>
                <SolutionImage>
                  <img src={solution.image} alt={copy.title} />
                </SolutionImage>
                <SolutionContent>
                  <h3>{copy.title}</h3>
                  <p>{copy.description}</p>
                  <SolutionLink to={localePath(solution.link)}>{t.journeyLinkLabel}</SolutionLink>
                </SolutionContent>
              </SolutionCard>
            );
          })}
        </SolutionsGrid>

        <SectionLabel>{t.modulesLabel}</SectionLabel>
        <ModuleGrid>
          {modules.map((module) => (
            <ModuleCard key={module.slug} to={localePath(`/products/${module.slug}`)}>
              <span>{module.shortName}</span>
              <h3>{module.name}</h3>
              <p>{module.tagline}</p>
            </ModuleCard>
          ))}
        </ModuleGrid>

        <CtaBanner
          title={t.ctaTitle}
          description={t.ctaDescription}
          primaryLabel={t.ctaPrimaryLabel}
          primaryTo={localePath('/contact')}
          secondaryLabel={t.ctaSecondaryLabel}
          secondaryHref="https://docs.akas.co/"
        />
      </SolutionsContainer>
    </>
  );
};

const SolutionsContainer = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 2.5rem 1rem 4rem;
`;

const Intro = styled.header`
  max-width: 760px;
  margin: 0 auto 2.75rem;
  text-align: center;
`;

const Kicker = styled.p`
  margin: 0 0 0.5rem;
  color: var(--primary-color);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const PageTitle = styled.h1`
  font-size: clamp(2rem, 4vw, 2.7rem);
  color: var(--text-dark);
  letter-spacing: -0.03em;
  margin-bottom: 0.85rem;
`;

const Lead = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: 1.08rem;
  line-height: 1.7;
`;

const SectionLabel = styled.h2`
  font-size: 1.35rem;
  color: var(--text-dark);
  margin: 2.5rem 0 1.1rem;
`;

const SolutionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const SolutionCard = styled.div`
  background: white;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid var(--border);
`;

const SolutionImage = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const SolutionContent = styled.div`
  padding: 1.4rem 1.35rem 1.5rem;

  h3 {
    font-size: 1.4rem;
    color: var(--text-dark);
    margin-bottom: 0.65rem;
  }

  p {
    color: var(--text-muted);
    margin-bottom: 1.15rem;
    line-height: 1.6;
  }
`;

const SolutionLink = styled(Link)`
  display: inline-flex;
  padding: 0.5rem 1rem;
  background-color: var(--primary-color);
  color: white;
  font-weight: 650;
  border-radius: 999px;

  &:hover {
    background-color: var(--primary-hover);
  }
`;

const ModuleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.9rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ModuleCard = styled(Link)`
  display: block;
  padding: 1.15rem 1.15rem 1.2rem;
  border: 1px solid var(--border);
  border-radius: 16px;
  background: var(--surface);

  span {
    color: var(--primary-color);
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.07em;
    text-transform: uppercase;
  }

  h3 {
    margin: 0.35rem 0 0.35rem;
    font-size: 1.05rem;
    color: var(--text-dark);
  }

  p {
    margin: 0;
    color: var(--text-muted);
    font-size: 0.92rem;
    line-height: 1.5;
  }

  &:hover {
    border-color: var(--primary-color);
  }
`;

export default SolutionsPage;
